import {
  ORDERING_MODE,
  orderingModeAllowsOrdering,
  resolveOrderingMode,
} from "./deliverySchedule";

export const normalizeRestaurantAvailability = (payload) => {
  if (!payload || typeof payload !== "object") return null;
  // `available` is the current contract and must win even if a legacy alias is
  // stale or contradictory. Only consult aliases when the field is absent.
  const available =
    typeof payload.available === "boolean"
      ? payload.available
      : payload.isOpen === true || payload.isCurrentlyOpen === true;
  return {
    ...payload,
    available,
    isOpen: available,
    code: payload.code || (available ? "VENDOR_OPEN" : "VENDOR_CLOSED"),
    message:
      payload.message ||
      (available
        ? "This vendor is accepting orders now."
        : "This vendor is not accepting orders right now."),
    nextOpening: payload.nextOpening || null,
  };
};

/**
 * Trading policy per vertical. Mirrors the backend
 * (services/vendorAvailabilityService.js, payload field `tradingPolicy`) and the
 * mobile client, so all three agree on what happens when hours are missing or
 * the hours check itself cannot be completed.
 *
 * - open_until_set: retail/wholesale, brand and healthcare shops are open 24/7
 *   until the vendor publishes hours. If the /open-status request fails, the
 *   storefront keeps trading; order-create re-checks hours server-side anyway.
 * - quiet_until_published: restaurants trade only inside published hours, so an
 *   unconfirmed check stays fail-closed on the client.
 */
export const TRADING_POLICY = {
  OPEN_UNTIL_SET: "open_until_set",
  QUIET_UNTIL_PUBLISHED: "quiet_until_published",
};

// The aliases the rest of the app already passes around for each vertical
// (see resolveBase in hooks/useRestaurantAvailability.js).
const OPEN_UNTIL_SET_TYPES = new Set([
  "retailer",
  "rw",
  "wholesaler",
  "retail",
  "brand",
  "publication",
  "healthcare",
  "pharmacy",
  "health",
]);

export const tradingPolicyFor = (vendorType) =>
  OPEN_UNTIL_SET_TYPES.has(
    String(vendorType || "")
      .trim()
      .toLowerCase()
  )
    ? TRADING_POLICY.OPEN_UNTIL_SET
    : TRADING_POLICY.QUIET_UNTIL_PUBLISHED;

export const keepsTradingWhenUnknown = (vendorType) =>
  tradingPolicyFor(vendorType) === TRADING_POLICY.OPEN_UNTIL_SET;

export const UNCONFIRMED_HOURS_MESSAGE =
  "We couldn't confirm this shop's hours, but orders are still accepted.";

export const availabilityMessage = (
  { status, availability, error } = {},
  vendorType
) => {
  if (status === "loading" || status === "idle") {
    return "Confirming opening hours…";
  }
  if (status === "error") {
    // Unknown-but-trading: the request failed, but this vertical is open by
    // policy, so say so instead of telling the shopper to retry before ordering.
    if (keepsTradingWhenUnknown(vendorType)) return UNCONFIRMED_HOURS_MESSAGE;
    return (
      error ||
      "We can’t confirm this vendor’s hours right now. Retry before ordering."
    );
  }
  return availability?.message || "This vendor is not accepting orders.";
};

// Restaurant-typed alias, kept for existing call sites. Fail-closed on error.
export const restaurantAvailabilityLabel = (state) =>
  availabilityMessage(state, "restaurant");

/**
 * Whether the shopper may order given the current hook state and the vendor's
 * trading policy. A settled success is the only source of a confirmed verdict.
 * When the check itself failed, the answer comes from the policy: open-until-set
 * verticals keep trading, restaurants stay closed. Anything else (idle, loading)
 * is not an answer yet and stays closed.
 */
export const canOrderFromVendor = (
  { status, availability } = {},
  vendorType
) => {
  // A settled verdict goes through the one shared decision: open vendors and
  // closed vendors that take scheduled orders both let the shopper order.
  if (status === "success") {
    if (!availability) return false;
    if (availability.available === true) return true;
    return orderingModeAllowsOrdering(resolveOrderingMode(availability));
  }
  if (status === "error") return keepsTradingWhenUnknown(vendorType);
  return false;
};

// Restaurant-typed alias, kept for existing call sites. Fail-closed on error.
export const canOrderFromRestaurant = (state) =>
  canOrderFromVendor(state, "restaurant");

/**
 * A re-check is NOT a reset.
 *
 * Only the very first load is allowed to present as "checking". Once a verdict
 * has settled, revalidating keeps that verdict on screen and merely raises
 * `revalidating` — otherwise every pre-action re-check (add to cart, proceed to
 * checkout) would drop `canOrder` to false for a beat and flash "Checking
 * hours" chips across the whole menu.
 *
 * Safety is unaffected: callers gate on the awaited response, not on this
 * interim state, so the order path stays fail-closed.
 */
export const beginRevalidation = (state = {}) =>
  state.status === "loading" || state.status === "idle"
    ? { ...state, status: "loading", error: null, revalidating: true }
    : { ...state, revalidating: true };

/**
 * The awaited result of `availability.refresh()` (an open-status payload, or
 * the hook's error-shaped fallback): may the shopper carry on? Pre-action gates
 * (add to cart, checkout, shipping, payment, place order) call this instead of
 * reading `available`, so a vendor that is closed now but takes scheduled
 * orders is not stopped.
 */
export const availabilityAllowsOrdering = (latest) => {
  if (!latest) return false;
  if (latest.available === true) return true;
  return resolveOrderingMode(latest) === ORDERING_MODE.SCHEDULED_ONLY;
};

/**
 * Closed right now but taking orders for later (open-status
 * `scheduledOrdersOnly`). Storefronts show a calm notice instead of the closed
 * banner, and checkout forces a scheduled delivery time.
 */
export const isScheduledOrdersOnly = ({ status, availability } = {}) =>
  status === "success" &&
  resolveOrderingMode(availability) === ORDERING_MODE.SCHEDULED_ONLY;
